/**
 * OpenAI Responses API subset used by the desktop Rumi runner AND the sim. `store` is always false
 * (spec section 11). Objects are `.loose()` to tolerate additional real-API fields.
 */
import { z } from 'zod';

/** Origin of the OpenAI API: the only inference destination allowed outside an explicitly permitted stand-in. */
export const OPENAI_API_ORIGIN = 'https://api.openai.com';
/** Base URL a key registered in Admin → API keys gets when none is given, in every server mode. */
export const OPENAI_API_BASE_URL = `${OPENAI_API_ORIGIN}/v1`;

function originOf(value: string): string | null {
  try {
    return new URL(value).origin;
  } catch {
    return null;
  }
}

/**
 * Why a base URL may not receive an OpenAI secret, or null when it may. Permitted: https to
 * api.openai.com, or one of `allowedOrigins` (the local sim's origin during development and in
 * the test suites); never credentials, a query or a fragment. The server checks registered keys
 * with it and the desktop worker checks every redeemed grant before the secret leaves the machine.
 */
export function openAiBaseUrlProblem(baseUrl: string, allowedOrigins: readonly string[] = []): string | null {
  let url: URL;
  try {
    url = new URL(baseUrl);
  } catch {
    return 'is not a valid URL';
  }
  if (url.username || url.password) return 'must not carry credentials';
  if (url.search || url.hash) return 'must not carry a query or fragment';
  const allowed = new Set<string>([OPENAI_API_ORIGIN]);
  for (const origin of allowedOrigins) {
    const o = originOf(origin);
    if (o) allowed.add(o);
  }
  if (!allowed.has(url.origin)) return `points at ${url.origin}, which is not an allowed inference destination`;
  return null;
}

export function isPermittedOpenAiBaseUrl(baseUrl: string, allowedOrigins: readonly string[] = []): boolean {
  return openAiBaseUrlProblem(baseUrl, allowedOrigins) === null;
}

export const OPENAI_ENDPOINTS = {
  responses: '/v1/responses',
  responseCancel: '/v1/responses/{responseId}/cancel',
  models: '/v1/models',
} as const;

// ---------------------------------------------------------------------------------------------
// Request
// ---------------------------------------------------------------------------------------------

export const OpenAIInputTextSchema = z.object({ type: z.literal('input_text'), text: z.string() });
export const OpenAIOutputTextRefSchema = z.object({ type: z.literal('output_text'), text: z.string() });

export const OpenAIInputMessageSchema = z.object({
  type: z.literal('message').optional(),
  role: z.enum(['user', 'assistant', 'system', 'developer']),
  phase: z.enum(['commentary', 'final_answer']).nullish(),
  content: z.union([z.string(), z.array(z.union([OpenAIInputTextSchema, OpenAIOutputTextRefSchema]))]),
});
export type OpenAIInputMessage = z.infer<typeof OpenAIInputMessageSchema>;

/** Echo of a prior model function call (needed when replaying history with store:false). */
export const OpenAIFunctionCallInputSchema = z.object({
  type: z.literal('function_call'),
  id: z.string().optional(),
  call_id: z.string(),
  name: z.string(),
  arguments: z.string(),
});

export const OpenAIFunctionCallOutputSchema = z.object({
  type: z.literal('function_call_output'),
  call_id: z.string(),
  output: z.string(),
});
export type OpenAIFunctionCallOutput = z.infer<typeof OpenAIFunctionCallOutputSchema>;

/** Opaque reasoning can be replayed between tool calls without storing responses at OpenAI. */
export const OpenAIInputReasoningSchema = z.object({
  type: z.literal('reasoning'),
  id: z.string(),
  summary: z.array(z.object({ type: z.literal('summary_text'), text: z.string() })).default([]),
  encrypted_content: z.string().nullish(),
});

export const OpenAIInputItemSchema = z.union([
  OpenAIInputMessageSchema,
  OpenAIFunctionCallInputSchema,
  OpenAIFunctionCallOutputSchema,
  OpenAIInputReasoningSchema,
]);
export type OpenAIInputItem = z.infer<typeof OpenAIInputItemSchema>;

export const OpenAIFunctionToolSchema = z.object({
  type: z.literal('function'),
  name: z.string().regex(/^[a-zA-Z0-9_-]{1,64}$/),
  description: z.string().optional(),
  parameters: z.record(z.string(), z.unknown()),
  strict: z.boolean().optional(),
});
export type OpenAIFunctionTool = z.infer<typeof OpenAIFunctionToolSchema>;

export const OpenAIToolChoiceSchema = z.union([
  z.enum(['auto', 'none', 'required']),
  z.object({ type: z.literal('function'), name: z.string() }),
]);
export type OpenAIToolChoice = z.infer<typeof OpenAIToolChoiceSchema>;

export const OpenAIReasoningConfigSchema = z.object({
  effort: z.enum(['minimal', 'low', 'medium', 'high']).optional(),
  summary: z.enum(['auto', 'concise', 'detailed']).optional(),
});

export const OpenAIResponsesRequestSchema = z.object({
  model: z.string().min(1),
  input: z.union([z.string(), z.array(OpenAIInputItemSchema)]),
  instructions: z.string().optional(),
  tools: z.array(OpenAIFunctionToolSchema).optional(),
  tool_choice: OpenAIToolChoiceSchema.optional(),
  parallel_tool_calls: z.boolean().optional(),
  stream: z.boolean().optional(),
  /** Opt-in response fields. `reasoning.encrypted_content` is returned only when asked for, and with
   *  `store: false` it is the only form of reasoning a later request in the same turn can replay. */
  include: z.array(z.enum(['reasoning.encrypted_content'])).optional(),
  /** Always false: conversation state lives on the Mac. */
  store: z.literal(false),
  max_output_tokens: z.number().int().positive().optional(),
  temperature: z.number().min(0).max(2).optional(),
  reasoning: OpenAIReasoningConfigSchema.optional(),
  text: z.object({ format: z.object({ type: z.literal('json_schema'), name: z.string(), strict: z.literal(true), schema: z.record(z.string(), z.unknown()) }) }).optional(),
  truncation: z.enum(['auto', 'disabled']).optional(),
  /** Allowlisted metadata only (operation id, account id hash); never customer content. */
  metadata: z.record(z.string().max(64), z.string().max(512)).optional(),
  user: z.string().max(128).optional(),
});
export type OpenAIResponsesRequest = z.infer<typeof OpenAIResponsesRequestSchema>;

// ---------------------------------------------------------------------------------------------
// Response
// ---------------------------------------------------------------------------------------------

export const OpenAIAnnotationSchema = z
  .object({
    type: z.string(),
    start_index: z.number().int().optional(),
    end_index: z.number().int().optional(),
    url: z.string().optional(),
    title: z.string().optional(),
  })
  .loose();

export const OpenAIOutputTextSchema = z
  .object({
    type: z.literal('output_text'),
    text: z.string(),
    annotations: z.array(OpenAIAnnotationSchema).default([]),
  })
  .loose();
export const OpenAIRefusalSchema = z.object({ type: z.literal('refusal'), refusal: z.string() }).loose();
export const OpenAIOutputContentSchema = z.discriminatedUnion('type', [OpenAIOutputTextSchema, OpenAIRefusalSchema]);
export type OpenAIOutputContent = z.infer<typeof OpenAIOutputContentSchema>;

export const OPENAI_ITEM_STATUSES = ['in_progress', 'completed', 'incomplete'] as const;
export const OpenAIItemStatusSchema = z.enum(OPENAI_ITEM_STATUSES);

export const OpenAIOutputMessageSchema = z
  .object({
    type: z.literal('message'),
    id: z.string(),
    role: z.literal('assistant'),
    phase: z.enum(['commentary', 'final_answer']).nullish(),
    status: OpenAIItemStatusSchema,
    content: z.array(OpenAIOutputContentSchema),
  })
  .loose();
export type OpenAIOutputMessage = z.infer<typeof OpenAIOutputMessageSchema>;

export const OpenAIOutputFunctionCallSchema = z
  .object({
    type: z.literal('function_call'),
    id: z.string(),
    call_id: z.string(),
    name: z.string(),
    arguments: z.string(),
    status: OpenAIItemStatusSchema.optional(),
  })
  .loose();
export type OpenAIOutputFunctionCall = z.infer<typeof OpenAIOutputFunctionCallSchema>;

export const OpenAIOutputReasoningSchema = z
  .object({
    type: z.literal('reasoning'),
    id: z.string(),
    summary: z.array(z.object({ type: z.literal('summary_text'), text: z.string() }).loose()).default([]),
    encrypted_content: z.string().nullish(),
    status: OpenAIItemStatusSchema.optional(),
  })
  .loose();

export const OpenAIOutputItemSchema = z.discriminatedUnion('type', [
  OpenAIOutputMessageSchema,
  OpenAIOutputFunctionCallSchema,
  OpenAIOutputReasoningSchema,
]);
export type OpenAIOutputItem = z.infer<typeof OpenAIOutputItemSchema>;

export const OpenAIUsageSchema = z
  .object({
    input_tokens: z.number().int().nonnegative(),
    output_tokens: z.number().int().nonnegative(),
    total_tokens: z.number().int().nonnegative().optional(),
    input_tokens_details: z.object({ cached_tokens: z.number().int().nonnegative() }).loose().optional(),
    /** reasoning tokens are included in output_tokens; never add them twice (spec section 17). */
    output_tokens_details: z.object({ reasoning_tokens: z.number().int().nonnegative() }).loose().optional(),
  })
  .loose();
export type OpenAIUsage = z.infer<typeof OpenAIUsageSchema>;

export const OPENAI_RESPONSE_STATUSES = ['completed', 'failed', 'in_progress', 'cancelled', 'queued', 'incomplete'] as const;
export const OpenAIResponseStatusSchema = z.enum(OPENAI_RESPONSE_STATUSES);
export type OpenAIResponseStatus = z.infer<typeof OpenAIResponseStatusSchema>;

export const OpenAIResponseErrorSchema = z.object({ code: z.string(), message: z.string() }).loose();

export const OpenAIResponseSchema = z
  .object({
    id: z.string(),
    object: z.literal('response'),
    created_at: z.number(),
    status: OpenAIResponseStatusSchema,
    model: z.string(),
    output: z.array(OpenAIOutputItemSchema).default([]),
    usage: OpenAIUsageSchema.optional(),
    error: OpenAIResponseErrorSchema.nullable().optional(),
    incomplete_details: z.object({ reason: z.string() }).loose().nullable().optional(),
    metadata: z.record(z.string(), z.string()).nullable().optional(),
    store: z.boolean().optional(),
    max_output_tokens: z.number().int().nullable().optional(),
  })
  .loose();
export type OpenAIResponse = z.infer<typeof OpenAIResponseSchema>;

/** Error envelope returned with 4xx/5xx status. */
export const OpenAIErrorEnvelopeSchema = z
  .object({
    error: z
      .object({
        message: z.string(),
        type: z.string().optional(),
        code: z.string().nullable().optional(),
        param: z.string().nullable().optional(),
      })
      .loose(),
  })
  .loose();
export type OpenAIErrorEnvelope = z.infer<typeof OpenAIErrorEnvelopeSchema>;

// ---------------------------------------------------------------------------------------------
// Streaming events (server-sent events; `event:` name equals `type`)
// ---------------------------------------------------------------------------------------------

const seq = z.number().int().nonnegative().optional();

export const OpenAIStreamEventSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('response.created'), sequence_number: seq, response: OpenAIResponseSchema }).loose(),
  z.object({ type: z.literal('response.in_progress'), sequence_number: seq, response: OpenAIResponseSchema }).loose(),
  z.object({ type: z.literal('response.output_item.added'), sequence_number: seq, output_index: z.number().int(), item: OpenAIOutputItemSchema }).loose(),
  z.object({ type: z.literal('response.output_item.done'), sequence_number: seq, output_index: z.number().int(), item: OpenAIOutputItemSchema }).loose(),
  z.object({ type: z.literal('response.content_part.added'), sequence_number: seq, item_id: z.string(), output_index: z.number().int(), content_index: z.number().int(), part: OpenAIOutputContentSchema }).loose(),
  z.object({ type: z.literal('response.content_part.done'), sequence_number: seq, item_id: z.string(), output_index: z.number().int(), content_index: z.number().int(), part: OpenAIOutputContentSchema }).loose(),
  z.object({ type: z.literal('response.output_text.delta'), sequence_number: seq, item_id: z.string(), output_index: z.number().int(), content_index: z.number().int(), delta: z.string() }).loose(),
  z.object({ type: z.literal('response.output_text.done'), sequence_number: seq, item_id: z.string(), output_index: z.number().int(), content_index: z.number().int(), text: z.string() }).loose(),
  z.object({ type: z.literal('response.refusal.delta'), sequence_number: seq, item_id: z.string(), output_index: z.number().int(), content_index: z.number().int(), delta: z.string() }).loose(),
  z.object({ type: z.literal('response.refusal.done'), sequence_number: seq, item_id: z.string(), output_index: z.number().int(), content_index: z.number().int(), refusal: z.string() }).loose(),
  z.object({ type: z.literal('response.function_call_arguments.delta'), sequence_number: seq, item_id: z.string(), output_index: z.number().int(), delta: z.string() }).loose(),
  z.object({ type: z.literal('response.function_call_arguments.done'), sequence_number: seq, item_id: z.string(), output_index: z.number().int(), arguments: z.string() }).loose(),
  z.object({ type: z.literal('response.reasoning_summary_text.delta'), sequence_number: seq, item_id: z.string(), output_index: z.number().int(), summary_index: z.number().int(), delta: z.string() }).loose(),
  z.object({ type: z.literal('response.reasoning_summary_text.done'), sequence_number: seq, item_id: z.string(), output_index: z.number().int(), summary_index: z.number().int(), text: z.string() }).loose(),
  z.object({ type: z.literal('response.completed'), sequence_number: seq, response: OpenAIResponseSchema }).loose(),
  z.object({ type: z.literal('response.incomplete'), sequence_number: seq, response: OpenAIResponseSchema }).loose(),
  z.object({ type: z.literal('response.failed'), sequence_number: seq, response: OpenAIResponseSchema }).loose(),
  z.object({ type: z.literal('error'), sequence_number: seq, code: z.string().nullable().optional(), message: z.string(), param: z.string().nullable().optional() }).loose(),
]);
export type OpenAIStreamEvent = z.infer<typeof OpenAIStreamEventSchema>;
export type OpenAIStreamEventType = OpenAIStreamEvent['type'];

/** Streaming event types the runner must treat as terminal. */
export const OPENAI_TERMINAL_STREAM_EVENTS: readonly OpenAIStreamEventType[] = [
  'response.completed',
  'response.incomplete',
  'response.failed',
  'error',
];

/** Local Rumi tool names (spec section 11). Inputs are validated outside the model. */
export const RUMI_TOOL_NAMES = [
  'searchRecords',
  'getRecord',
  'getRelatedFindings',
  'countByStatus',
  'compareSnapshots',
  'getCoverageManifest',
] as const;
export const RumiToolNameSchema = z.enum(RUMI_TOOL_NAMES);
export type RumiToolName = z.infer<typeof RumiToolNameSchema>;

/** Model list response (used by the sim and by admin model-config validation). */
export const OpenAIModelListSchema = z
  .object({
    object: z.literal('list'),
    data: z.array(z.object({ id: z.string(), object: z.literal('model'), created: z.number().optional(), owned_by: z.string().optional() }).loose()),
  })
  .loose();
